import React, { forwardRef } from 'react';
import classNames from 'classnames';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  hoverable?: boolean;
  compact?: boolean;
  borderless?: boolean;
  overflow?: boolean;
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ children, className, hoverable = true, compact = false, borderless = false, overflow = true, ...rest }, ref) => {
    const tableClass = classNames(
      'ecme-table',
      {
        'ecme-table-hoverable': hoverable,
        'ecme-table-compact': compact,
        'ecme-table-borderless': borderless,
      },
      className
    );

    const table = (
      <table ref={ref} className={tableClass} {...rest}>
        {children}
      </table>
    );

    if (overflow) {
      return <div className="ecme-table-wrapper overflow-x-auto">{table}</div>;
    }

    return table;
  }
);

Table.displayName = 'Table';

export interface THeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const THead = forwardRef<HTMLTableSectionElement, THeadProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <thead ref={ref} className={classNames('ecme-thead', className)} {...rest}>
        {children}
      </thead>
    );
  }
);

THead.displayName = 'THead';

export interface TBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TBody = forwardRef<HTMLTableSectionElement, TBodyProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <tbody ref={ref} className={classNames('ecme-tbody', className)} {...rest}>
        {children}
      </tbody>
    );
  }
);

TBody.displayName = 'TBody';

export interface TFootProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TFoot = forwardRef<HTMLTableSectionElement, TFootProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <tfoot ref={ref} className={classNames('ecme-tfoot', className)} {...rest}>
        {children}
      </tfoot>
    );
  }
);

TFoot.displayName = 'TFoot';

export interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> {}

export const Tr = forwardRef<HTMLTableRowElement, TrProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <tr ref={ref} className={classNames('ecme-tr', className)} {...rest}>
        {children}
      </tr>
    );
  }
);

Tr.displayName = 'Tr';

export interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {}

export const Th = forwardRef<HTMLTableCellElement, ThProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <th ref={ref} className={classNames('ecme-th', className)} {...rest}>
        {children}
      </th>
    );
  }
);

Th.displayName = 'Th';

export interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {}

export const Td = forwardRef<HTMLTableCellElement, TdProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <td ref={ref} className={classNames('ecme-td', className)} {...rest}>
        {children}
      </td>
    );
  }
);

Td.displayName = 'Td';

export default Table;
